import { useState } from 'react';
import {
  Stack,
  Group,
  Button,
  Badge,
  Text,
  ActionIcon,
  Tooltip,
  Title,
} from '@mantine/core';
import { PiPlus, PiPencil, PiTrash } from 'react-icons/pi';
import {
  GenericTable,
  GenericTableColumn,
} from '@/components/GenericTable/GenericTable';
import { useRoles } from '@/models/adminUsers';
import type { Role } from '@/types/admin';
import { useHasPermission } from '@/models/permissions';
import { PERMISSIONS } from '@/models/permissions';
import { RoleFormModal } from '@/components/admin/RoleFormModal';
import { DeleteRoleConfirmationModal } from '@/components/admin/DeleteRoleConfirmationModal';

export default function Roles() {
  const canWrite = useHasPermission(PERMISSIONS.RBAC_WRITE);
  const { data, isLoading } = useRoles();

  // Modal states
  const [roleFormModalOpened, setRoleFormModalOpened] = useState(false);
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const handleCreateRole = () => {
    setSelectedRole(null);
    setIsEditMode(false);
    setRoleFormModalOpened(true);
  };

  const handleEditRole = (role: Role) => {
    setSelectedRole(role);
    setIsEditMode(true);
    setRoleFormModalOpened(true);
  };

  const handleDeleteRole = (role: Role) => {
    setSelectedRole(role);
    setDeleteModalOpened(true);
  };

  // Column definitions
  const columns: GenericTableColumn<Role>[] = [
    {
      accessor: 'name',
      title: 'Role Name',
      sortable: false,
      width: 200,
      render: (role) => (
        <Text size="sm" fw={600}>
          {role.name}
        </Text>
      ),
    },
    {
      accessor: 'description',
      title: 'Description',
      sortable: false,
      render: (role) => (
        <Tooltip
          label={role.description}
          disabled={!role.description || role.description.length < 80}
        >
          <Text size="sm" lineClamp={2} style={{ maxWidth: 400 }}>
            {role.description || '—'}
          </Text>
        </Tooltip>
      ),
    },
    {
      accessor: 'permissions',
      title: 'Permissions',
      sortable: false,
      width: 150,
      render: (role) => (
        <Badge size="lg" variant="light" color="blue">
          {role.permissions?.length || 0} permissions
        </Badge>
      ),
    },
    {
      accessor: 'id',
      title: 'Actions',
      sortable: false,
      width: 120,
      render: (role) => (
        <Group gap="xs" wrap="nowrap">
          {canWrite && (
            <>
              <Tooltip label="Edit Role">
                <ActionIcon
                  variant="subtle"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditRole(role);
                  }}
                >
                  <PiPencil size={16} />
                </ActionIcon>
              </Tooltip>
              {role.name !== 'Admin' && (
                <Tooltip label="Delete Role">
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteRole(role);
                    }}
                  >
                    <PiTrash size={16} />
                  </ActionIcon>
                </Tooltip>
              )}
            </>
          )}
        </Group>
      ),
    },
  ];

  return (
    <Stack gap="md" p="md">
      {/* Header */}
      <Group justify="space-between">
        <Title order={2}>Role Management</Title>
        {canWrite && (
          <Button leftSection={<PiPlus />} onClick={handleCreateRole}>
            Create Role
          </Button>
        )}
      </Group>

      {/* Roles Table */}
      <GenericTable<Role>
        records={data?.roles || []}
        columns={columns}
        loading={isLoading}
        emptyMessage="No roles found"
        idAccessor="id"
      />

      {/* Modals */}
      <RoleFormModal
        opened={roleFormModalOpened}
        onClose={() => setRoleFormModalOpened(false)}
        role={selectedRole}
        isEditMode={isEditMode}
      />
      <DeleteRoleConfirmationModal
        opened={deleteModalOpened}
        onClose={() => setDeleteModalOpened(false)}
        role={selectedRole}
      />
    </Stack>
  );
}
